import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu,
  Typography,
  Stack,
  CircularProgress,
  IconButton as MuiIconButton,
  Tabs,
  Tab,
  Button,
  Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  SportsEsports as SportsEsportsIcon,
  Warning as WarningIcon,
  WatchLater as WatchLaterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useNotifications from '../hooks/useNotifications';
import PendingMatchCard from './PendingMatchCard';
import DenyMatchCard from './DenyMatchCard';

const NotificationMenu = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [skipTransition, setSkipTransition] = useState(false);
  const isMounted = useRef(true);
  
  const { 
    notificationCount, 
    matches = [], 
    rejectedMatches = [],
    usersMap = {}, 
    loading: isLoading = false, 
    isConnected,
    handleApprove = () => {}, 
    handleReject = () => {}, 
    fetchPendingMatches = () => {},
    fetchRejectedMatches = () => {},
    handleOk = () => {},
    handleCreateNew = () => {}
  } = useNotifications();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleMenuOpen = useCallback(async (event) => {
    if (!isMounted.current) return;

    try {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
      setScrollPosition(currentScroll);
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.overflow = 'hidden';
      setAnchorEl(event.currentTarget);

      if (isConnected) {
        await Promise.all([
          fetchPendingMatches(),
          fetchRejectedMatches()
        ]);
      }
    } catch (error) {
      console.error('Error al abrir el menú:', error);
    }
  }, [isConnected, fetchPendingMatches, fetchRejectedMatches]);

  const handleMenuClose = useCallback((skipAnim = false) => {
    setSkipTransition(skipAnim);
    setAnchorEl(null);
    requestAnimationFrame(() => {
      if (!isMounted.current) return;
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('top');
      window.scrollTo(0, scrollPosition);
      setSkipTransition(false);
    });
  }, [scrollPosition]);

  const handleMatchClick = useCallback(async (e, match) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/match/${match.id}`);
    handleMenuClose(true);
  }, [navigate, handleMenuClose]);

  const handleCreateNewSuccess = useCallback(() => {
    handleMenuClose(true);
  }, [handleMenuClose]);

  const handleUpdate = useCallback(async () => {
    await Promise.all([
      fetchPendingMatches(),
      fetchRejectedMatches()
    ]);
  }, [fetchPendingMatches, fetchRejectedMatches]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          cursor: 'pointer',
          outline: 'none',
          '&:focus': {
            outline: 'none'
          }
        }}
        onClick={handleMenuOpen}
        tabIndex={0}
        role="button"
        aria-label="Abrir notificaciones"
      >
        <Box sx={{ position: 'relative' }}>
          <NotificationsIcon sx={{ fontSize: 25, color: 'text.secondary' }} />
          {notificationCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'error.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
            >
              {notificationCount}
            </Box>
          )}
          {!isConnected && notificationCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'warning.main',
                width: 8,
                height: 8,
                borderRadius: '50%'
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          component="span"
          sx={{
            fontSize: '0.79rem',
            mt: -0.5,
            color: 'text.secondary'
          }}
        >
          Notif.
        </Typography>
      </Box>
      {Boolean(anchorEl) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1299,
            transition: skipTransition ? 'none' : 'opacity 100ms ease-in-out',
            opacity: Boolean(anchorEl) ? 1 : 0
          }}
          onClick={() => handleMenuClose(false)}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          handleMenuClose(false);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: '80vh',
            overflow: 'auto',
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
        MenuListProps={{
          sx: {
            p: 0
          }
        }}
        keepMounted
      >
        <Box 
          sx={{ 
            p: 1.5, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '40px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: '#c89e38',
                fontSize: '1rem',
                lineHeight: 1.2,
                '&.MuiTypography-root': {
                  color: '#c89e38 !important'
                }
              }}
            >
              Última notificación
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMenuClose(true);
                navigate('/notifications');
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                },
                minWidth: 'auto',
                py: 0.5
              }}
            >
              Ver Todas
            </Button>
          </Box>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMenuClose(false);
            }}
            sx={{ 
              width: '32px',
              height: '32px',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </IconButton>
        </Box>
        
        <Box 
          sx={{ 
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1, 
            p: 1.5,
            height: 'calc(100% - 40px)',
            '& > *': {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0
            },
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : matches.length === 0 && rejectedMatches.length === 0 ? (
            <Box sx={{ 
              py: 2,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 1
            }}>
              <SportsEsportsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography color="text.secondary">
                No hay notificaciones pendientes
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {matches.length > 0 ? (
                <>
                  <Chip
                    label="Partida Pendiente"
                    size="small"
                    icon={<WatchLaterIcon />}
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'rgba(255, 152, 0, 0.75)',
                      border: '1px solid',
                      borderColor: 'rgba(255, 152, 0, 0.75)',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      alignSelf: 'flex-start',
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      },
                      '& .MuiChip-icon': {
                        color: 'rgba(255, 152, 0, 0.75)'
                      }
                    }}
                  />
                  <PendingMatchCard
                    key={`pending-${matches[0].id}`}
                    match={matches[0]}
                    usersMap={usersMap}
                    onClick={handleMatchClick}
                    onUpdate={handleUpdate}
                    showApprovalButtons={true}
                  />
                </>
              ) : rejectedMatches.length > 0 ? (
                <>
                  <Chip
                    label="Partida Rechazada"
                    size="small"
                    icon={<CloseIcon />}
                    sx={{
                      bgcolor: '#000000',
                      color: 'rgba(244, 67, 54, 0.75)',
                      border: '1px solid rgba(244, 67, 54, 0.75)',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      alignSelf: 'flex-start',
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: '#1a1a1a'
                      },
                      '& .MuiChip-icon': {
                        color: 'rgba(244, 67, 54, 0.75)'
                      }
                    }}
                  />
                  <DenyMatchCard
                    key={`rejected-${rejectedMatches[0].id}`}
                    match={rejectedMatches[0]}
                    onUpdate={handleUpdate}
                    onClick={handleMatchClick}
                    onCreateNewSuccess={handleCreateNewSuccess}
                  />
                </>
              ) : null}
              {(matches.length > 0 || rejectedMatches.length > 0) && (
                <>
                  <Box sx={{ my: 2, borderTop: '1px solid', borderColor: 'divider' }} />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMenuClose(true);
                      navigate('/notifications');
                    }}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }}
                  >
                    Ver todas las notificaciones ({notificationCount})
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default React.memo(NotificationMenu); 